import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { format, getUser } from "@gamedev-web/gamedev-sdk-core";
import { HubInvitationStatus } from "@gamedev-web/one-gaming-crm";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Warning } from "@gaming/xds-react-js-icons";

import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerAccountIconImage } from "../../components/PartnerAccountIconImage";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import {
  enabledQueryParameterValue,
  invitationSources,
  partnerQueryParameters,
  redirectToSignIn,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { formatShortDate } from "../../helpers/dateHelpers";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { useStrings } from "../../localization/localizationHelpers";
import type { PartnerInvitation, PartnerInvitationAction } from "../../models/partnerInvitations";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import {
  getXboxOnboardData,
  getXboxOnboardIsLoading,
} from "../../redux/selectors/xboxOnboardSelectors";
import { InvitationForm } from "../AdminPage/components/InviteContactModal";
import { InvitationConfirmationModal } from "./InvitationConfirmationModal";
import * as styles from "./PartnerInvitationsPage.module.css";

const invitationsApiUrl = `${xboxOnboardSiteLocationPrefix}/invitations/api`;
const acceptanceDelayMs = 3000;
const acceptancePollingAttempts = 20;
const createCompanyChoice = "create-company";
const continueCurrentChoice = "continue-current";
type InvitationDataResponse = Readonly<{
  invitations: PartnerInvitation[];
  canCreateOrganization?: boolean;
  canContinueToDashboard?: boolean;
  currentAccount?: Readonly<{
    accountId: string;
    name: string;
    gameConceptCount: number;
    pitchCount: number;
  }>;
}>;

type InvitationCardProps = Readonly<{
  invitation: PartnerInvitation;
  isUpdating: boolean;
  hideBack: boolean;
  onBack: () => void;
  onJoin: (invitation: PartnerInvitationAction & { action: "accept" }) => void;
  onReject: () => void;
}>;

type PendingConfirmation =
  | Readonly<{
      action: "accept";
      invitation: PartnerInvitation;
      invitationAction: PartnerInvitationAction & { action: "accept" };
    }>
  | Readonly<{
      action: "reject";
      invitation: PartnerInvitation;
    }>;

const InvitationCard = ({
  invitation,
  isUpdating,
  hideBack,
  onBack,
  onJoin,
  onReject,
}: InvitationCardProps) => {
  const [
    invitationDescription,
    invitedByLabel,
    expiresLabel,
    doesNotExpireLabel,
    joinLabel,
    rejectLabel,
    backLabel,
  ] = useStrings(
    "partnerInvitationsDescription",
    "partnerInvitationsInvitedBy",
    "partnerAdminInvitationExpiresLabel",
    "partnerInvitationsDoesNotExpireLabel",
    "partnerInvitationsJoinLabel",
    "partnerInvitationsRejectLabel",
    "back"
  );

  return (
    <div className={styles.invitationFormContainer}>
      <InvitationForm
        resetKey={invitation.invitationId}
        initialInvitation={{
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          jobTitle: invitation.jobTitle,
          email: invitation.email,
          accessLevel: invitation.accessLevel,
        }}
        title={
          <span className={styles.reviewHeading}>
            <span className={styles.choiceImage}>
              <PartnerAccountIconImage accountId={invitation.accountId} />
            </span>
            <span>{invitation.companyName}</span>
          </span>
        }
        description={format(invitationDescription, invitation.companyName)}
        metadata={
          <div className={styles.invitationMetadata}>
            <p className={styles.invitedBy}>
              {invitedByLabel}: {invitation.invitedBy} ·{" "}
              <a className={styles.inviterEmail} href={`mailto:${invitation.inviterEmail}`}>
                {invitation.inviterEmail}
              </a>
            </p>
            <p className={styles.expiration}>
              {expiresLabel}:{" "}
              {invitation.expiresOn ? (
                <time dateTime={invitation.expiresOn}>{formatShortDate(invitation.expiresOn)}</time>
              ) : (
                doesNotExpireLabel
              )}
            </p>
          </div>
        }
        submitLabel={joinLabel}
        cancelLabel={backLabel}
        hideCancel={hideBack}
        secondaryActionLabel={rejectLabel}
        isDisabled={isUpdating}
        isEmailReadOnly={true}
        roleMode="assigned"
        allowInternalScroll={false}
        hideOrganizationEmailNotice={true}
        onCancel={onBack}
        onSecondaryAction={onReject}
        onSubmit={async details => {
          onJoin({
            action: "accept",
            firstName: details.firstName,
            lastName: details.lastName,
            jobTitle: details.jobTitle,
          });
        }}
      />
    </div>
  );
};

export const PartnerInvitationsPage = () => {
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const xboxOnboardData = useSelector(getXboxOnboardData);
  const isXboxOnboardLoading = useSelector(getXboxOnboardIsLoading);
  const history = useHistory();
  const location = useLocation();
  const source = new URLSearchParams(location.search).get(partnerQueryParameters.invitationSource);
  const enteredFromDashboard = source === invitationSources.dashboard;
  const enteredFromEmail = source === invitationSources.email;
  const { invitationId: routeInvitationId } = useParams<{ invitationId?: string }>();
  const invitationListUrl = `${xboxOnboardSiteLocationPrefix}/invitations${location.search}`;
  const [invitations, setInvitations] = useState<PartnerInvitation[] | null>(null);
  const [updatingInvitationId, setUpdatingInvitationId] = useState("");
  const [acceptingCompany, setAcceptingCompany] = useState("");
  const [joinedCompany, setJoinedCompany] = useState("");
  const [hasError, setHasError] = useState(false);
  const [isInvitationUnavailable, setIsInvitationUnavailable] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState("");
  const [canCreateOrganization, setCanCreateOrganization] = useState(true);
  const [canContinueToDashboard, setCanContinueToDashboard] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<InvitationDataResponse["currentAccount"]>();
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [
    breadcrumbLabel,
    title,
    description,
    reviewTitle,
    reviewDescription,
    emailReviewDescription,
    selectionTitle,
    joinOrganizationLabel,
    createCompanyLabel,
    createCompanyDescription,
    continueToDashboardLabel,
    continueToDashboardDescription,
    transferAccessWarning,
    continueOnboardingLabel,
    continueOnboardingDescription,
    orLabel,
    accountLimitLabel,
    continueLabel,
    invitedByLabel,
    expiresLabel,
    doesNotExpireLabel,
    joinedAnnouncement,
    emptyLabel,
    loadingLabel,
    acceptingLabel,
    errorLabel,
    unavailableTitle,
    unavailableLabel,
    backLabel,
    cancelLabel,
    joinLabel,
    rejectLabel,
    confirmAcceptTitle,
    confirmAcceptDescription,
    confirmAcceptExistingAccountDescription,
    transferTitle,
    transferWarning,
    acceptTransferLabel,
    confirmRejectTitle,
    confirmRejectDescription,
  ] = useStrings(
    "partnerInvitationsLabel",
    "partnerInvitationsPageTitle",
    "partnerInvitationsPageDescription",
    "partnerInvitationsReviewTitle",
    "partnerInvitationsReviewDescription",
    "partnerInvitationsEmailReviewDescription",
    "partnerInvitationsSelectionTitle",
    "partnerInvitationsJoinOrganizationLabel",
    "partnerInvitationsCreateCompanyLabel",
    "partnerInvitationsCreateCompanyDescription",
    "partnerInvitationsContinueToDashboardLabel",
    "partnerInvitationsContinueToDashboardDescription",
    "partnerInvitationsTransferAccessWarning",
    "partnerInvitationsContinueOnboardingLabel",
    "partnerInvitationsContinueOnboardingDescription",
    "partnerInvitationsOrLabel",
    "partnerInvitationsAccountLimitLabel",
    "continue",
    "partnerInvitationsInvitedBy",
    "partnerAdminInvitationExpiresLabel",
    "partnerInvitationsDoesNotExpireLabel",
    "partnerInvitationsJoinedAnnouncement",
    "partnerInvitationsEmptyLabel",
    "partnerInvitationsLoadingLabel",
    "partnerInvitationsAcceptingLabel",
    "partnerInvitationsErrorLabel",
    "partnerInvitationsUnavailableTitle",
    "partnerInvitationsUnavailableLabel",
    "back",
    "cancel",
    "partnerInvitationsJoinLabel",
    "partnerInvitationsRejectLabel",
    "partnerInvitationsConfirmAcceptTitle",
    "partnerInvitationsConfirmAcceptDescription",
    "partnerInvitationsConfirmAcceptExistingAccountDescription",
    "partnerInvitationsTransferTitle",
    "partnerInvitationsTransferWarning",
    "partnerInvitationsAcceptTransferLabel",
    "partnerInvitationsConfirmRejectTitle",
    "partnerInvitationsConfirmRejectDescription"
  );

  useEffect(() => {
    if (!isSignedIn || !user?.emailAddress) {
      return;
    }

    let active = true;
    fetch(`${invitationsApiUrl}/data`)
      .then(response => {
        if (response.status === 401 || response.redirected) {
          redirectToSignIn();
          return null;
        }
        if (response.status === 403) {
          history.replace(`${xboxOnboardSiteLocationPrefix}/dashboard`);
          return null;
        }
        if (!response.ok) {
          throw new Error(`Failed to load invitations: ${response.status}`);
        }
        return response.json() as Promise<InvitationDataResponse>;
      })
      .then(data => {
        if (active && data) {
          setInvitations(data.invitations);
          setCanCreateOrganization(data.canCreateOrganization ?? true);
          setCanContinueToDashboard(data.canContinueToDashboard ?? false);
          setCurrentAccount(data.currentAccount);
        }
      })
      .catch(() => {
        if (active) {
          setHasError(true);
        }
      });

    return () => {
      active = false;
    };
  }, [history, isSignedIn, user?.emailAddress]);

  useEffect(() => {
    if (isSignedIn && currentAccount && !xboxOnboardData && !isXboxOnboardLoading) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [currentAccount, dispatch, isSignedIn, isXboxOnboardLoading, xboxOnboardData]);

  const updateInvitation = async (
    invitationId: string,
    action: PartnerInvitationAction
  ): Promise<boolean> => {
    const response = await fetch(`${invitationsApiUrl}/${invitationId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(action),
    });
    if (response.status === 401 || response.redirected) {
      redirectToSignIn();
      throw new Error("Authentication required");
    }
    if (response.status === 403) {
      history.replace(`${xboxOnboardSiteLocationPrefix}/dashboard`);
      throw new Error("Contact already exists");
    }
    if (response.status === 404 || response.status === 409) {
      return false;
    }
    if (!response.ok) {
      throw new Error(`Failed to update invitation: ${response.status}`);
    }
    return true;
  };

  const getInvitationStatus = async (invitationId: string): Promise<HubInvitationStatus> => {
    const response = await fetch(`${invitationsApiUrl}/${invitationId}/status`, {
      cache: "no-store",
    });
    if (response.status === 401 || response.redirected) {
      redirectToSignIn();
      throw new Error("Authentication required");
    }
    if (!response.ok) {
      throw new Error(`Failed to check invitation status: ${response.status}`);
    }
    const { status } = (await response.json()) as { status: HubInvitationStatus };
    return status;
  };

  const waitForInvitationOnboarding = async (invitationId: string): Promise<boolean> => {
    for (let attempt = 0; attempt < acceptancePollingAttempts; attempt += 1) {
      const status = await getInvitationStatus(invitationId);
      if (status === HubInvitationStatus.Onboarded) {
        return true;
      }
      await new Promise<void>(resolve => setTimeout(resolve, acceptanceDelayMs));
    }
    return false;
  };

  const join = async (
    invitation: PartnerInvitation,
    action: PartnerInvitationAction & { action: "accept" }
  ) => {
    setUpdatingInvitationId(invitation.invitationId);
    setAcceptingCompany(invitation.companyName);
    setHasError(false);
    setIsInvitationUnavailable(false);
    try {
      const updated = await updateInvitation(invitation.invitationId, action);
      const currentStatus = updated
        ? undefined
        : await getInvitationStatus(invitation.invitationId);
      if (
        !updated &&
        currentStatus !== HubInvitationStatus.Accepted &&
        currentStatus !== HubInvitationStatus.Onboarded
      ) {
        setSelectedChoice("");
        setInvitations(
          current => current?.filter(item => item.invitationId !== invitation.invitationId) ?? []
        );
        setIsInvitationUnavailable(true);
        if (routeInvitationId) {
          history.replace(invitationListUrl);
        }
        return;
      }
      if (await waitForInvitationOnboarding(invitation.invitationId)) {
        window.location.replace(`${xboxOnboardSiteLocationPrefix}/dashboard`);
        return;
      }
      setInvitations([]);
      setJoinedCompany(invitation.companyName);
    } catch {
      setHasError(true);
    } finally {
      setAcceptingCompany("");
      setUpdatingInvitationId("");
    }
  };

  const reject = async (invitationId: string) => {
    setUpdatingInvitationId(invitationId);
    setHasError(false);
    try {
      await updateInvitation(invitationId, { action: "reject" });
      setSelectedChoice(current => (current === invitationId ? "" : current));
      setInvitations(
        current => current?.filter(invitation => invitation.invitationId !== invitationId) ?? []
      );
      if (routeInvitationId) {
        history.replace(invitationListUrl);
      }
    } catch {
      setHasError(true);
    } finally {
      setUpdatingInvitationId("");
    }
  };

  const continueWithSelection = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedChoice === createCompanyChoice) {
      history.push(
        `${xboxOnboardSiteLocationPrefix}/new?${partnerQueryParameters.skipInvitation}=${enabledQueryParameterValue}`
      );
      return;
    }
    if (selectedChoice === continueCurrentChoice) {
      if (currentAccount) {
        history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
      } else {
        history.push(`${xboxOnboardSiteLocationPrefix}/new`, { skipInvitation: true });
      }
      return;
    }
    if (invitations?.some(invitation => invitation.invitationId === selectedChoice)) {
      history.push(
        `${xboxOnboardSiteLocationPrefix}/invitations/${encodeURIComponent(selectedChoice)}${
          location.search
        }`
      );
      setSelectedChoice("");
    }
  };

  const confirmPendingAction = async () => {
    const confirmation = pendingConfirmation;
    setPendingConfirmation(null);
    if (!confirmation) {
      return;
    }
    if (confirmation.action === "accept") {
      await join(confirmation.invitation, confirmation.invitationAction);
    } else {
      await reject(confirmation.invitation.invitationId);
    }
  };

  const confirmationCompany = pendingConfirmation?.invitation.companyName ?? "";
  const confirmationTitle =
    pendingConfirmation?.action === "accept"
      ? currentAccount
        ? transferTitle
        : format(confirmAcceptTitle, confirmationCompany)
      : format(confirmRejectTitle, confirmationCompany);
  const confirmationDescription =
    pendingConfirmation?.action === "accept"
      ? currentAccount
        ? format(confirmAcceptExistingAccountDescription, currentAccount.name, confirmationCompany)
        : format(confirmAcceptDescription, confirmationCompany)
      : format(confirmRejectDescription, confirmationCompany);
  const confirmationLabel =
    pendingConfirmation?.action === "accept"
      ? currentAccount
        ? acceptTransferLabel
        : joinLabel
      : rejectLabel;
  const transfer =
    pendingConfirmation?.action === "accept" && currentAccount
      ? {
          sourceName: currentAccount.name,
          sourceAccountId: currentAccount.accountId,
          targetName: confirmationCompany,
          targetAccountId: pendingConfirmation.invitation.accountId,
          invitedBy: pendingConfirmation.invitation.invitedBy,
          invitedByLabel,
          warning: format(transferWarning, currentAccount.name),
        }
      : undefined;
  const selectedInvitation = invitations?.find(
    invitation => invitation.invitationId === routeInvitationId
  );
  const alternativeOption = canContinueToDashboard
    ? {
        choice: continueCurrentChoice,
        label: currentAccount ? continueToDashboardLabel : continueOnboardingLabel,
        description: currentAccount
          ? format(continueToDashboardDescription, currentAccount.name)
          : continueOnboardingDescription,
      }
    : currentAccount || !canCreateOrganization
    ? undefined
    : {
        choice: createCompanyChoice,
        label: createCompanyLabel,
        description: createCompanyDescription,
      };
  const invitationNotFound = Boolean(routeInvitationId && !selectedInvitation && !joinedCompany);
  const returnToInvitationList = () => {
    history.push(invitationListUrl);
  };
  const cancelInvitationSelection = () => {
    history.replace(
      enteredFromDashboard || currentAccount
        ? `${xboxOnboardSiteLocationPrefix}/dashboard`
        : `${xboxOnboardSiteLocationPrefix}/new?${partnerQueryParameters.skipInvitation}=${enabledQueryParameterValue}`
    );
  };
  const leaveUnavailableInvitation = () => {
    setIsInvitationUnavailable(false);
    if (invitations?.length) {
      history.replace(invitationListUrl);
      return;
    }
    cancelInvitationSelection();
  };

  if (
    !isSignedIn ||
    !user?.emailAddress ||
    invitations === null ||
    (Boolean(currentAccount) && !xboxOnboardData)
  ) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={enteredFromDashboard ? breadcrumbLabel : undefined}
        showSectionNavigation={enteredFromDashboard}
      >
        <div role="status" aria-label={loadingLabel} aria-busy="true">
          <PageLoadingSpinner />
        </div>
      </PartnerPageLayout>
    );
  }

  if (isInvitationUnavailable || invitationNotFound) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={enteredFromDashboard ? breadcrumbLabel : undefined}
        showSectionNavigation={enteredFromDashboard}
      >
        <section
          className={styles.unavailable}
          role="alert"
          aria-labelledby="invitation-unavailable-title"
        >
          <img
            className={styles.unavailableImage}
            src={getImage("404_space", ImageType.Png)}
            alt=""
            aria-hidden="true"
          />
          <h1 id="invitation-unavailable-title" className={styles.unavailableTitle}>
            {unavailableTitle}
          </h1>
          <p className={styles.unavailableDescription}>{unavailableLabel}</p>
          <BasicButton
            as="button"
            buttonType={ButtonType.Brand}
            className={styles.unavailableAction}
            type="button"
            onClick={leaveUnavailableInvitation}
          >
            {backLabel}
          </BasicButton>
        </section>
      </PartnerPageLayout>
    );
  }

  return (
    <PartnerPageLayout
      breadcrumbLabel={enteredFromDashboard ? breadcrumbLabel : undefined}
      showSectionNavigation={enteredFromDashboard}
    >
      <div className={styles.page} data-review={Boolean(selectedInvitation)}>
        {invitations.length > 0 && (
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>{selectedInvitation ? reviewTitle : title}</h1>
          </div>
        )}
        {(!enteredFromDashboard || selectedInvitation) && (
          <p className={styles.description}>
            {selectedInvitation
              ? format(
                  enteredFromEmail ? emailReviewDescription : reviewDescription,
                  selectedInvitation.companyName
                )
              : description}
          </p>
        )}
        <section aria-label={selectedInvitation ? reviewTitle : selectionTitle}>
          {hasError && (
            <p className={styles.empty} role="alert">
              {errorLabel}
            </p>
          )}
          {acceptingCompany ? (
            <div role="status" aria-live="polite" aria-label={acceptingLabel} aria-busy="true">
              <PageLoadingSpinner />
            </div>
          ) : (
            <>
              <p className={styles.announcement} aria-live="polite">
                {joinedCompany && format(joinedAnnouncement, joinedCompany)}
              </p>
              {invitationNotFound ? null : invitations.length === 0 ? (
                <p className={styles.empty}>{emptyLabel}</p>
              ) : selectedInvitation ? (
                <InvitationCard
                  key={selectedInvitation.invitationId}
                  invitation={selectedInvitation}
                  isUpdating={Boolean(updatingInvitationId)}
                  hideBack={enteredFromEmail}
                  onBack={returnToInvitationList}
                  onJoin={invitationAction => {
                    setPendingConfirmation({
                      action: "accept",
                      invitation: selectedInvitation,
                      invitationAction,
                    });
                  }}
                  onReject={() => {
                    setPendingConfirmation({ action: "reject", invitation: selectedInvitation });
                  }}
                />
              ) : (
                <form className={styles.selectionPanel} onSubmit={continueWithSelection}>
                  <h2 className={styles.selectionTitle}>{selectionTitle}</h2>
                  {canContinueToDashboard && currentAccount && (
                    <div className={styles.transferWarning}>
                      <Icon
                        aria-hidden="true"
                        className={styles.transferWarningIcon}
                        icon={Warning}
                        iconSize={IconSize.Custom}
                      />
                      <span>{format(transferAccessWarning, currentAccount.name)}</span>
                    </div>
                  )}
                  <fieldset className={styles.choiceGroup}>
                    <legend className={styles.choiceGroupLabel}>{joinOrganizationLabel}</legend>
                    <div className={styles.choiceOptions}>
                      {invitations.map(invitation => (
                        <label
                          key={invitation.invitationId}
                          className={styles.choiceOption}
                          htmlFor={`invitation-choice-${invitation.invitationId}`}
                          aria-label={invitation.companyName}
                        >
                          <span className={styles.choiceImage}>
                            <PartnerAccountIconImage accountId={invitation.accountId} />
                          </span>
                          <span className={styles.choiceDetails}>
                            <span className={styles.choiceTitle}>{invitation.companyName}</span>
                            <span className={styles.choiceDescription}>
                              {invitedByLabel}: {invitation.invitedBy}
                            </span>
                            <span className={styles.choiceDescription}>
                              {expiresLabel}:{" "}
                              {invitation.expiresOn
                                ? formatShortDate(invitation.expiresOn)
                                : doesNotExpireLabel}
                            </span>
                          </span>
                          <input
                            id={`invitation-choice-${invitation.invitationId}`}
                            type="radio"
                            name="invitation-choice"
                            value={invitation.invitationId}
                            checked={selectedChoice === invitation.invitationId}
                            onChange={event => setSelectedChoice(event.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                    {alternativeOption && (
                      <>
                        <div className={styles.choiceOr}>{orLabel}</div>
                        <label
                          className={styles.choiceOption}
                          htmlFor="invitation-choice-alternative"
                          aria-label={alternativeOption.label}
                        >
                          <span className={styles.choiceDetails}>
                            <span className={styles.choiceTitle}>{alternativeOption.label}</span>
                            <span className={styles.choiceDescription}>
                              {alternativeOption.description}
                            </span>
                          </span>
                          <input
                            id="invitation-choice-alternative"
                            type="radio"
                            name="invitation-choice"
                            value={alternativeOption.choice}
                            checked={selectedChoice === alternativeOption.choice}
                            onChange={event => setSelectedChoice(event.target.value)}
                          />
                        </label>
                      </>
                    )}
                  </fieldset>
                  <div className={styles.selectionFooter}>
                    {!enteredFromDashboard && (
                      <p className={styles.accountLimit}>{accountLimitLabel}</p>
                    )}
                    <div className={styles.selectionActions}>
                      <BasicButton
                        as="button"
                        buttonType={ButtonType.Secondary}
                        type="button"
                        disabled={Boolean(updatingInvitationId)}
                        onClick={cancelInvitationSelection}
                      >
                        {cancelLabel}
                      </BasicButton>
                      <BasicButton
                        as="button"
                        buttonType={ButtonType.Brand}
                        type="submit"
                        disabled={Boolean(updatingInvitationId) || !selectedChoice}
                      >
                        {continueLabel}
                      </BasicButton>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </section>
        <InvitationConfirmationModal
          isOpen={Boolean(pendingConfirmation)}
          title={confirmationTitle}
          description={confirmationDescription}
          confirmLabel={confirmationLabel}
          cancelLabel={cancelLabel}
          transfer={transfer}
          onConfirm={confirmPendingAction}
          onCancel={() => setPendingConfirmation(null)}
        />
      </div>
    </PartnerPageLayout>
  );
};
